import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import bg1 from "./assets/1.png";
import bg2 from "./assets/2.png";
import bg3 from "./assets/3.png";
import bg4 from "./assets/4.png";
import soundtrack from "./assets/soundtrack.mp3";

const COLORS = {
  bg: 0x0a0015,
  grid: 0xff00ff,
  gridEmit: 0x8800ff,
  player: 0x00ffff,
  playerEmit: 0x0088ff,
  bullet: 0xffff00,
  bulletEmit: 0xff8800,
  enemy: 0xff0055,
  enemyEmit: 0x880022,
  explosion: 0xff6600,
  sun: 0xff00aa,
  sunEmit: 0x8800ff,
  star: 0xffffff,
  ocean: 0x1a0033,
};

  const W = 800, H = 600;
  const GRID_SIZE = 40;
  const ENEMY_ROWS = 3;
  const SCROLL_SPEED = 0.08;
  const LEVEL_DURATION_SECONDS = 30;
  const TARGET_FPS = 60;
  const LEVEL_DURATION_FRAMES = LEVEL_DURATION_SECONDS * TARGET_FPS;
  const WAVE_INTERVAL_SECONDS = 5;
  const ENEMY_ENTRY_DURATION_SECONDS = 0.2;
  const POWERUP_SPAWN_INTERVAL_SECONDS = 12;
  const POWERUP_SPEED = 0.08;
  const FAN_SHOT_DURATION_SECONDS = 8;

// Renders the 1943 arcade-style shooter game.
export default function Game1943() {
  const mountRef = useRef(null);
  const gameRef = useRef({});
  const keysRef = useRef({});
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameState, setGameState] = useState("start"); // start | playing | dead | win
  const [highScore, setHighScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [levelTimeLeft, setLevelTimeLeft] = useState(LEVEL_DURATION_SECONDS);
  const [levelComplete, setLevelComplete] = useState(false);
  const [autoFireEnabled, setAutoFireEnabled] = useState(false);
  const [soundtrackEnabled, setSoundtrackEnabled] = useState(true);
  const [deathMessageVisible, setDeathMessageVisible] = useState(false);
  const animRef = useRef(null);
  const autoFireRef = useRef(false);
  const soundtrackRef = useRef(null);
  const audioContextRef = useRef(null);
  const sfxUnlockedRef = useRef(false);
  const levelCompleteSfxPlayedRef = useRef(false);
  const gameOverSfxPlayedRef = useRef(false);
  const deathMessageTimeoutRef = useRef(null);
  const touchStateRef = useRef({
    active: false,
    touchId: null,
    target: { x: 0, y: 0 },
  });

  // Updates the auto-fire state when the toggle changes.
  const handleAutoFireToggle = useCallback((event) => {
    setAutoFireEnabled(event.target.checked);
  }, []);

  // Updates the soundtrack toggle state when the user changes the control.
  const handleSoundtrackToggle = useCallback((event) => {
    setSoundtrackEnabled(event.target.checked);
  }, []);

  // Shows the death message briefly when the player loses a life.
  const triggerDeathMessage = useCallback(() => {
    setDeathMessageVisible(true);
    if (deathMessageTimeoutRef.current) {
      clearTimeout(deathMessageTimeoutRef.current);
    }
    deathMessageTimeoutRef.current = setTimeout(() => {
      setDeathMessageVisible(false);
    }, 1200);
  }, []);

  // Keeps the auto-fire ref in sync for the game loop.
  useEffect(() => {
    autoFireRef.current = autoFireEnabled;
  }, [autoFireEnabled]);

  // Initializes the soundtrack audio element for playback control.
  useEffect(() => {
    const audio = new Audio(soundtrack);
    audio.loop = true;
    audio.preload = "auto";
    soundtrackRef.current = audio;

    return () => {
      if (soundtrackRef.current) {
        soundtrackRef.current.pause();
        soundtrackRef.current.currentTime = 0;
      }
      soundtrackRef.current = null;
    };
  }, []);

  // Syncs soundtrack playback with the current game state and toggle choice.
  useEffect(() => {
    const audio = soundtrackRef.current;
    if (!audio) return;

    if (gameState === "playing" && soundtrackEnabled) {
      audio.play().catch(() => {});
      return;
    }

    audio.pause();
    audio.currentTime = 0;
  }, [gameState, soundtrackEnabled]);

  // Creates or returns the shared AudioContext for synthesized SFX.
  const getOrCreateAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Ensures the AudioContext is resumed after a user interaction to unlock SFX.
  const unlockSfxAudio = useCallback(() => {
    const context = getOrCreateAudioContext();
    if (context.state === "suspended") {
      context.resume().catch(() => {});
    }
    sfxUnlockedRef.current = true;
  }, [getOrCreateAudioContext]);

  // Plays a short 8-bit style beep for the requested SFX type.
  const playSfx = useCallback((type) => {
    if (!sfxUnlockedRef.current) return;
    const context = getOrCreateAudioContext();
    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const settings = {
      enemy: { frequency: 220, duration: 0.12, type: "square" },
      player: { frequency: 110, duration: 0.28, type: "square" },
      powerup: { frequency: 660, duration: 0.18, type: "square" },
      levelComplete: { frequency: 880, duration: 0.32, type: "triangle" },
      gameOver: { frequency: 70, duration: 0.5, type: "sawtooth" },
    };
    const config = settings[type] || settings.enemy;

    oscillator.type = config.type;
    oscillator.frequency.setValueAtTime(config.frequency, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + config.duration);

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + config.duration + 0.02);
  }, [getOrCreateAudioContext]);

  // Plays the level complete sound once per completed level.
  const triggerLevelCompleteSfx = useCallback(() => {
    if (levelCompleteSfxPlayedRef.current) return;
    levelCompleteSfxPlayedRef.current = true;
    playSfx("levelComplete");
  }, [playSfx]);

  // Plays the game over sound once per game over transition.
  const triggerGameOverSfx = useCallback(() => {
    if (gameOverSfxPlayedRef.current) return;
    gameOverSfxPlayedRef.current = true;
    playSfx("gameOver");
  }, [playSfx]);

  // Disposes the SFX AudioContext when the component unmounts.
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, []);

  // Resets the game state and triggers a restart.
  const startGame = useCallback(() => {
    unlockSfxAudio();
    levelCompleteSfxPlayedRef.current = false;
    gameOverSfxPlayedRef.current = false;
    setDeathMessageVisible(false);
    setScore(0);
    setLives(3);
    setLevel(1);
    setLevelTimeLeft(LEVEL_DURATION_SECONDS);
    setLevelComplete(false);
    setGameState("playing");
    gameRef.current.restart = true;
  }, [unlockSfxAudio]);

  // Ensures the game over sound triggers when the state changes to dead.
  useEffect(() => {
    if (gameState === "dead") {
      triggerGameOverSfx();
    }
  }, [gameState, triggerGameOverSfx]);

  // Initializes and runs the Three.js game scene when playing.
  useEffect(() => {
    if (gameState !== "playing") return;

    const container = mountRef.current;
    if (!container) return;

    // --- RENDERER ---
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    container.appendChild(renderer.domElement);

    // --- SCENE & CAMERA ---
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(COLORS.bg);
    scene.fog = new THREE.FogExp2(COLORS.bg, 0.018);

    const camera = new THREE.OrthographicCamera(-10, 10, 7.5, -7.5, 0.1, 100);
    camera.position.set(0, 0, 20);
    camera.lookAt(0, 0, 0);

    // --- LIGHTS ---
    const ambient = new THREE.AmbientLight(0x220033, 2);
    scene.add(ambient);
    const pinkLight = new THREE.PointLight(0xff00ff, 3, 30);
    pinkLight.position.set(0, 5, 10);
    scene.add(pinkLight);
    const cyanLight = new THREE.PointLight(0x00ffff, 2, 20);
    cyanLight.position.set(-5, -3, 10);
    scene.add(cyanLight);

    // --- SCROLLING BACKGROUND (full screen, top to bottom) ---
    const backgroundGroup = new THREE.Group();
    backgroundGroup.position.set(0, 0, -4);
    scene.add(backgroundGroup);
    const BG_LAYER_HEIGHT = 15;
    const BG_LAYER_HALF = BG_LAYER_HEIGHT / 2;
    const backgroundTextures = [bg1, bg2, bg3, bg4].map((src) => {
      const tex = new THREE.TextureLoader().load(src);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = false;
      tex.repeat.set(1, 1);
      tex.offset.set(0, 0);
      return tex;
    });

    // Creates a background plane for the scrolling backdrop at a fixed vertical offset.
    function createBackgroundPlane(material, yOffset) {
      const geometry = new THREE.PlaneGeometry(20, 15);
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(0, yOffset, 0);
      return mesh;
    }

    const backgroundMaterial = new THREE.MeshBasicMaterial({
      map: backgroundTextures[0],
    });
    let currentBackgroundIndex = 0;
    const backgroundPlane1 = createBackgroundPlane(backgroundMaterial, 0);
    const backgroundPlane2 = createBackgroundPlane(backgroundMaterial, BG_LAYER_HEIGHT);
    backgroundGroup.add(backgroundPlane1, backgroundPlane2);

    // Updates the background texture based on the current level without resetting scroll continuity.
    function setBackgroundForLevel(level) {
      const index = (level - 1) % backgroundTextures.length;
      if (index === currentBackgroundIndex) {
        return;
      }
      currentBackgroundIndex = index;
      backgroundMaterial.map = backgroundTextures[index];
      backgroundMaterial.needsUpdate = true;
    }

    // Repositions background planes to create a seamless scroll without UV offset jitter.
    function updateBackgroundPlanes() {
      const bgScrollSpeed = SCROLL_SPEED * 0.4;
      const lowerBound = -7.5 - BG_LAYER_HALF;
      backgroundPlane1.position.y -= bgScrollSpeed;
      backgroundPlane2.position.y -= bgScrollSpeed;
      if (backgroundPlane1.position.y <= lowerBound) {
        backgroundPlane1.position.y = backgroundPlane2.position.y + BG_LAYER_HEIGHT;
      }
      if (backgroundPlane2.position.y <= lowerBound) {
        backgroundPlane2.position.y = backgroundPlane1.position.y + BG_LAYER_HEIGHT;
      }
    }

    // --- STARS ---
    const starGeo = new THREE.BufferGeometry();
    const starPositions = [];
    for (let i = 0; i < 300; i++) {
      starPositions.push(
        (Math.random() - 0.5) * 40,
        (Math.random() - 0.5) * 30,
        Math.random() * -5
      );
    }
    starGeo.setAttribute("position", new THREE.Float32BufferAttribute(starPositions, 3));
    const starMat = new THREE.PointsMaterial({ color: 0xffffff, size: 0.06, transparent: true, opacity: 0.8 });
    const stars = new THREE.Points(starGeo, starMat);
    scene.add(stars);

    // --- SCROLLING CLOUDS (full screen, top to bottom) ---
    const cloudGroup = new THREE.Group();
    cloudGroup.position.set(0, 0, -2);
    scene.add(cloudGroup);
    const CLOUD_LAYER_HEIGHT = 40;
    const CLOUD_LAYER_HALF = CLOUD_LAYER_HEIGHT / 2;
    const cloudPalette = [0xffffff, 0xe6e6e6, 0xbfbfbf];

    // Creates a stylized cloud made of multiple puffs.
    // Creates a stylized cloud made of multiple puffs.
    function createCloud() {
      const group = new THREE.Group();
      const puffCount = 3 + Math.floor(Math.random() * 3);
      for (let i = 0; i < puffCount; i++) {
        const radius = 0.4 + Math.random() * 0.7;
        const geo = new THREE.SphereGeometry(radius, 10, 10);
        const mat = new THREE.MeshBasicMaterial({
          color: cloudPalette[Math.floor(Math.random() * cloudPalette.length)],
          transparent: true,
          opacity: 0.45,
        });
        const puff = new THREE.Mesh(geo, mat);
        puff.position.set(
          (Math.random() - 0.5) * 1.6,
          (Math.random() - 0.5) * 0.8,
          0
        );
        group.add(puff);
      }
      const scale = 0.8 + Math.random() * 1.8;
      group.scale.setScalar(scale);
      return group;
    }

    // Creates a cloud layer positioned at a vertical offset.
    // Creates a cloud layer positioned at a vertical offset.
    function createCloudLayer(yOffset) {
      const layer = new THREE.Group();
      layer.position.y = yOffset;
      const cloudCount = 10;
      for (let i = 0; i < cloudCount; i++) {
        const cloud = createCloud();
        cloud.position.set(
          (Math.random() - 0.5) * 20,
          (Math.random() - 0.5) * CLOUD_LAYER_HEIGHT,
          0
        );
        layer.add(cloud);
      }
      return layer;
    }

    const cloudLayer1 = createCloudLayer(0);
    const cloudLayer2 = createCloudLayer(-CLOUD_LAYER_HEIGHT);
    cloudGroup.add(cloudLayer1, cloudLayer2);

    // Updates the cloud layer positions and wraps them after they leave the screen.
    function updateCloudLayers() {
      const cloudScrollSpeed = SCROLL_SPEED * 0.6;
      const lowerBound = -7.5 - CLOUD_LAYER_HALF;
      cloudLayer1.position.y -= cloudScrollSpeed;
      cloudLayer2.position.y -= cloudScrollSpeed;
      if (cloudLayer1.position.y <= lowerBound) {
        cloudLayer1.position.y = cloudLayer2.position.y + CLOUD_LAYER_HEIGHT;
      }
      if (cloudLayer2.position.y <= lowerBound) {
        cloudLayer2.position.y = cloudLayer1.position.y + CLOUD_LAYER_HEIGHT;
      }
    }

    // --- PLAYER PLANE (Capcom style) ---
    // Builds the player airplane mesh group.
    function createPlayerPlane() {
      const group = new THREE.Group();

      const fuselageMat = new THREE.MeshStandardMaterial({
        color: 0x5a5f3c,
        emissive: 0x1c1b11,
        emissiveIntensity: 0.22,
        metalness: 0.25,
        roughness: 0.82,
      });
      const wingFabricMat = new THREE.MeshStandardMaterial({
        color: 0x6c7148,
        emissive: 0x1b1d12,
        emissiveIntensity: 0.18,
        metalness: 0.18,
        roughness: 0.88,
      });
      const woodMat = new THREE.MeshStandardMaterial({
        color: 0x8a5b2e,
        emissive: 0x2a1a0d,
        emissiveIntensity: 0.18,
        metalness: 0.12,
        roughness: 0.9,
      });
      const metalMat = new THREE.MeshStandardMaterial({
        color: 0x7e8389,
        emissive: 0x1a1d22,
        emissiveIntensity: 0.2,
        metalness: 0.65,
        roughness: 0.35,
      });

      // Fuselage body and rounded nose to resemble a WWI fighter.
      const bodyGeo = new THREE.BoxGeometry(0.32, 1.02, 0.18);
      const body = new THREE.Mesh(bodyGeo, fuselageMat);
      body.position.y = -0.02;
      group.add(body);

      const noseGeo = new THREE.CylinderGeometry(0.14, 0.16, 0.26, 14);
      const nose = new THREE.Mesh(noseGeo, metalMat);
      nose.rotation.x = Math.PI / 2;
      nose.position.y = 0.56;
      group.add(nose);

      // Biplane wings (upper and lower decks).
      const upperWingGeo = new THREE.BoxGeometry(1.55, 0.15, 0.08);
      const upperWing = new THREE.Mesh(upperWingGeo, wingFabricMat);
      upperWing.position.set(0, 0.2, 0);
      group.add(upperWing);

      const lowerWingGeo = new THREE.BoxGeometry(1.32, 0.13, 0.08);
      const lowerWing = new THREE.Mesh(lowerWingGeo, wingFabricMat);
      lowerWing.position.set(0, -0.08, 0);
      group.add(lowerWing);

      // Wing struts between the two wings.
      const strutGeo = new THREE.BoxGeometry(0.05, 0.28, 0.05);
      [
        [-0.48, 0.05],
        [0.48, 0.05],
      ].forEach(([x, y]) => {
        const strut = new THREE.Mesh(strutGeo, woodMat);
        strut.position.set(x, y, 0.01);
        group.add(strut);
      });

      // Cockpit hump.
      const cockpitGeo = new THREE.SphereGeometry(0.12, 10, 10);
      const cockpitMat = new THREE.MeshStandardMaterial({
        color: 0x2f3324,
        emissive: 0x11120d,
        emissiveIntensity: 0.2,
        metalness: 0.22,
        roughness: 0.72,
      });
      const cockpit = new THREE.Mesh(cockpitGeo, cockpitMat);
      cockpit.scale.set(1, 0.7, 1);
      cockpit.position.set(0, 0.06, 0.07);
      group.add(cockpit);

      // Tailplane and vertical rudder.
      const tailPlaneGeo = new THREE.BoxGeometry(0.62, 0.09, 0.06);
      const tailPlane = new THREE.Mesh(tailPlaneGeo, wingFabricMat);
      tailPlane.position.set(0, -0.48, 0);
      group.add(tailPlane);

      const rudderGeo = new THREE.BoxGeometry(0.09, 0.22, 0.06);
      const rudder = new THREE.Mesh(rudderGeo, fuselageMat);
      rudder.position.set(0, -0.58, 0);
      group.add(rudder);

      // Roundel-style marks on wings (simple red dots).
      const markGeo = new THREE.CylinderGeometry(0.08, 0.08, 0.02, 14);
      const markMat = new THREE.MeshStandardMaterial({
        color: 0xaa2d1e,
        emissive: 0x2d0d08,
        emissiveIntensity: 0.2,
        metalness: 0.2,
        roughness: 0.7,
      });
      const leftMark = new THREE.Mesh(markGeo, markMat);
      leftMark.rotation.x = Math.PI / 2;
      leftMark.position.set(-0.55, 0.28, 0.03);
      group.add(leftMark);
      const rightMark = new THREE.Mesh(markGeo, markMat);
      rightMark.rotation.x = Math.PI / 2;
      rightMark.position.set(0.55, 0.28, 0.03);
      group.add(rightMark);

      // Propeller hub and blades (instead of neon ring).
      const propHubGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.06, 10);
      const propHub = new THREE.Mesh(propHubGeo, metalMat);
      propHub.rotation.x = Math.PI / 2;
      propHub.position.y = 0.68;
      group.add(propHub);

      const bladeGeo = new THREE.BoxGeometry(0.06, 0.5, 0.03);
      const prop = new THREE.Group();
      const bladeA = new THREE.Mesh(bladeGeo, woodMat);
      bladeA.position.y = 0;
      prop.add(bladeA);
      const bladeB = new THREE.Mesh(bladeGeo, woodMat);
      bladeB.rotation.z = Math.PI / 2;
      prop.add(bladeB);
      prop.position.y = 0.7;
      group.add(prop);

      // Soft engine shimmer.
      const glowGeo = new THREE.SphereGeometry(0.14, 10, 10);
      const glowMat = new THREE.MeshBasicMaterial({ color: 0xffc266, transparent: true, opacity: 0.16 });
      const glow = new THREE.Mesh(glowGeo, glowMat);
      glow.position.y = 0.6;
      group.add(glow);

      // Exhaust trail
      const trailGeo = new THREE.ConeGeometry(0.07, 0.4, 8);
      const trailMat = new THREE.MeshBasicMaterial({ color: 0xff6600, transparent: true, opacity: 0.6 });
      const trail = new THREE.Mesh(trailGeo, trailMat);
      trail.rotation.z = Math.PI;
      trail.position.y = -0.7;
      group.add(trail);

      group.propeller = prop;
      group.trail = trail;
      return group;
    }

    // Builds an enemy airplane mesh group by type.
    function createEnemyPlane(type = 0) {
      const group = new THREE.Group();
      const colors = [
        { main: 0xff2bd1, emissive: 0x88004f, accent: 0xfff200, symbol: 0x00ffff },
        { main: 0x00ffe1, emissive: 0x006666, accent: 0xff0066, symbol: 0xffff00 },
        { main: 0xff7b00, emissive: 0x883300, accent: 0x8b00ff, symbol: 0x00ff66 },
        { main: 0x8b00ff, emissive: 0x440066, accent: 0x00ffea, symbol: 0xff0066 },
        { main: 0xff0066, emissive: 0x660022, accent: 0x00ff66, symbol: 0xffff00 },
        { main: 0x00ff66, emissive: 0x006633, accent: 0xff2bd1, symbol: 0x00ffff },
      ];
      const paletteIndex = Math.floor(Math.random() * colors.length);
      const c = colors[paletteIndex];

      const bodyGeo = new THREE.BoxGeometry(0.28, 0.8, 0.15);
      const bodyMat = new THREE.MeshStandardMaterial({ color: c.main, emissive: c.emissive, emissiveIntensity: 1.3, metalness: 0.6, roughness: 0.25 });
      const body = new THREE.Mesh(bodyGeo, bodyMat);
      group.add(body);

      const wingGeo = new THREE.BoxGeometry(1.2, 0.2, 0.08);
      const wingMat = new THREE.MeshStandardMaterial({ color: c.main, emissive: c.emissive, emissiveIntensity: 1, metalness: 0.7, roughness: 0.2 });
      const wings = new THREE.Mesh(wingGeo, wingMat);
      wings.position.y = 0.05;
      group.add(wings);

      // Adds neon stripes along the wing edges for a synthwave look.
      const stripeGeo = new THREE.BoxGeometry(0.1, 0.22, 0.09);
      const stripeMat = new THREE.MeshStandardMaterial({ color: c.accent, emissive: c.accent, emissiveIntensity: 1.6, metalness: 0.4, roughness: 0.3 });
      const leftStripe = new THREE.Mesh(stripeGeo, stripeMat);
      leftStripe.position.set(-0.6, 0.05, 0.01);
      group.add(leftStripe);
      const rightStripe = new THREE.Mesh(stripeGeo, stripeMat);
      rightStripe.position.set(0.6, 0.05, 0.01);
      group.add(rightStripe);

      const tailGeo = new THREE.BoxGeometry(0.5, 0.1, 0.06);
      const tail = new THREE.Mesh(tailGeo, wingMat);
      tail.position.y = 0.42;
      group.add(tail);

      // Adds a stylized neon symbol on the fuselage for variety.
      const symbolMat = new THREE.MeshStandardMaterial({ color: c.symbol, emissive: c.symbol, emissiveIntensity: 1.5, metalness: 0.4, roughness: 0.3 });
      if (type % 3 === 0) {
        const crossBarGeo = new THREE.BoxGeometry(0.28, 0.06, 0.02);
        const crossStemGeo = new THREE.BoxGeometry(0.06, 0.28, 0.02);
        const crossBar = new THREE.Mesh(crossBarGeo, symbolMat);
        const crossStem = new THREE.Mesh(crossStemGeo, symbolMat);
        crossBar.position.set(0, -0.05, 0.09);
        crossStem.position.set(0, -0.05, 0.09);
        group.add(crossBar, crossStem);
      } else if (type % 3 === 1) {
        const chevronGeo = new THREE.BoxGeometry(0.22, 0.05, 0.02);
        const chevronLeft = new THREE.Mesh(chevronGeo, symbolMat);
        const chevronRight = new THREE.Mesh(chevronGeo, symbolMat);
        chevronLeft.rotation.z = Math.PI / 6;
        chevronRight.rotation.z = -Math.PI / 6;
        chevronLeft.position.set(-0.06, -0.1, 0.09);
        chevronRight.position.set(0.06, -0.1, 0.09);
        group.add(chevronLeft, chevronRight);
      } else {
        const diamondGeo = new THREE.BoxGeometry(0.16, 0.16, 0.02);
        const diamond = new THREE.Mesh(diamondGeo, symbolMat);
        diamond.rotation.z = Math.PI / 4;
        diamond.position.set(0, -0.12, 0.09);
        group.add(diamond);
      }

      group.rotation.z = Math.PI; // face downward
      return group;
    }

    // --- GAME STATE ---
    let playerPos = { x: 0, y: -5 };
    let playerVel = { x: 0, y: 0 };
    let playerBullets = [];
    let enemyBullets = [];
    let enemies = [];
    let explosions = [];
    let invincibleTimer = 0;
    let fireTimer = 0;
    let enemyFireTimer = 0;
    let powerUpSpawnTimerSeconds = 0;
    let activePowerUp = null;
    let powerUpTimerSeconds = 0;
    let backgroundScrollOffset = 0;
    let cloudScrollOffset = 0;
    let scoreVal = 0;
    let livesVal = 3;
    let gifts = [];
    let waveTimer = 0;
    let waveTimerSeconds = 0;
    let waveCount = 0;
    let levelVal = 1;
    let levelElapsedSeconds = 0;
    let levelSecondsLeft = LEVEL_DURATION_SECONDS;
    let levelCompleted = false;
    let lastFrameTime = performance.now();
    let lastDisplayedSeconds = LEVEL_DURATION_SECONDS;
    let gameOver = false;
    let won = false;

    const player = createPlayerPlane();
    player.position.set(0, -5, 0);
    scene.add(player);

    // Builds a gift power-up mesh for the player to collect.
    function createGift() {
      const group = new THREE.Group();
      const boxGeo = new THREE.BoxGeometry(0.6, 0.6, 0.2);
      const boxMat = new THREE.MeshStandardMaterial({ color: 0xff00ff, emissive: 0x6600aa, emissiveIntensity: 1.2, metalness: 0.6, roughness: 0.2 });
      const box = new THREE.Mesh(boxGeo, boxMat);
      group.add(box);
      const ribbonGeo = new THREE.BoxGeometry(0.1, 0.6, 0.22);
      const ribbonMat = new THREE.MeshStandardMaterial({ color: 0x00ffff, emissive: 0x0088ff, emissiveIntensity: 1.4, metalness: 0.3, roughness: 0.4 });
      const ribbon = new THREE.Mesh(ribbonGeo, ribbonMat);
      group.add(ribbon);
      const ribbonCrossGeo = new THREE.BoxGeometry(0.6, 0.1, 0.22);
      const ribbonCross = new THREE.Mesh(ribbonCrossGeo, ribbonMat);
      group.add(ribbonCross);
      const glowGeo = new THREE.SphereGeometry(0.45, 12, 12);
      const glowMat = new THREE.MeshBasicMaterial({ color: 0xff66ff, transparent: true, opacity: 0.3 });
      const glow = new THREE.Mesh(glowGeo, glowMat);
      group.add(glow);
      group.userData = { glow };
      return group;
    }

    // Spawns a new wave of enemies and updates the level background.
    function spawnWave() {
      waveCount++;
      setBackgroundForLevel(levelVal);
      setLevel(levelVal);
      const targetCount = Math.max(1, levelVal + 2);
      const baseEnemyFireIntervalSeconds = 2;
      const fireIntervalSeconds = baseEnemyFireIntervalSeconds / Math.pow(1.2, levelVal - 1);
      const fireIntervalFrames = Math.max(15, Math.round(fireIntervalSeconds * TARGET_FPS));
      const cols = Math.min(7, Math.max(3, Math.ceil(Math.sqrt(targetCount))));
      const rows = Math.min(3, Math.ceil(targetCount / cols));
      // Spreads enemy spawn columns across most of the visible width so waves can reach screen edges.
      const spawnSpread = 17;
      let spawned = 0;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (spawned >= targetCount) break;
          const e = createEnemyPlane(r);
          const startX = cols === 1 ? 0 : -spawnSpread / 2 + (spawnSpread / (cols - 1)) * c;
          const startY = 8 + r * 1.5;
          e.position.set(startX, startY, 0);
          e.userData = {
            hp: 1,
            startX,
            startY,
            t: 0,
            speed: 0.8 + Math.random() * 0.5,
            pattern: Math.floor(Math.random() * 3),
            fireRate: fireIntervalFrames + Math.floor(Math.random() * fireIntervalFrames * 0.3),
            fireTimer: Math.floor(Math.random() * 200),
            type: r,
          };
          scene.add(e);
          enemies.push(e);
          spawned++;
        }
      }
    }

    spawnWave();

    // Spawns a bullet mesh and glow for either the player or enemies.
    function spawnBullet(x, y, vy, isEnemy) {
      const geo = new THREE.BoxGeometry(isEnemy ? 0.06 : 0.08, isEnemy ? 0.35 : 0.45, 0.05);
      const mat = new THREE.MeshBasicMaterial({
        color: isEnemy ? 0xff0066 : COLORS.bullet,
      });
      const b = new THREE.Mesh(geo, mat);
      b.position.set(x, y, 0);
      b.userData = { vy, isEnemy };
      scene.add(b);

      // Glow
      const glowGeo = new THREE.SphereGeometry(0.12, 8, 8);
      const glowMat = new THREE.MeshBasicMaterial({
        color: isEnemy ? 0xff0066 : 0xffff00,
        transparent: true,
        opacity: 0.4,
      });
      const glow = new THREE.Mesh(glowGeo, glowMat);
      glow.position.set(x, y, 0);
      scene.add(glow);
      b.userData.glow = glow;

      // Adds a rear flame glow for enemy missiles so they read as jet-propelled.
      if (isEnemy) {
        const flameGeo = new THREE.ConeGeometry(0.08, 0.32, 10);
        const flameMat = new THREE.MeshBasicMaterial({ color: 0xffaa00, transparent: true, opacity: 0.85 });
        const flame = new THREE.Mesh(flameGeo, flameMat);
        flame.rotation.z = Math.PI;
        flame.position.set(x, y + 0.32, 0);
        scene.add(flame);

        const flameGlowGeo = new THREE.SphereGeometry(0.18, 10, 10);
        const flameGlowMat = new THREE.MeshBasicMaterial({ color: 0xff6600, transparent: true, opacity: 0.45 });
        const flameGlow = new THREE.Mesh(flameGlowGeo, flameGlowMat);
        flameGlow.position.set(x, y + 0.38, 0);
        scene.add(flameGlow);

        b.userData.flame = flame;
        b.userData.flameGlow = flameGlow;
      }

      if (isEnemy) enemyBullets.push(b);
      else playerBullets.push(b);
      return b;
    }

    // Spawns an explosion particle effect at a position.
    function spawnExplosion(x, y) {
      const particles = [];
      for (let i = 0; i < 14; i++) {
        const geo = new THREE.SphereGeometry(0.07 + Math.random() * 0.12, 6, 6);
        const mat = new THREE.MeshBasicMaterial({
          color: Math.random() > 0.5 ? 0xff6600 : 0xffff00,
          transparent: true,
          opacity: 1,
        });
        const p = new THREE.Mesh(geo, mat);
        const angle = (i / 14) * Math.PI * 2;
        const speed = 0.04 + Math.random() * 0.08;
        p.position.set(x, y, 0);
        p.userData = {
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1,
        };
        scene.add(p);
        particles.push(p);
      }
      explosions.push(particles);
    }

    // Removes a bullet and its glow from the scene and tracking array.
    function removeBullet(arr, b) {
      scene.remove(b);
      if (b.userData.glow) scene.remove(b.userData.glow);
      if (b.userData.flame) scene.remove(b.userData.flame);
      if (b.userData.flameGlow) scene.remove(b.userData.flameGlow);
      const idx = arr.indexOf(b);
      if (idx > -1) arr.splice(idx, 1);
    }

    // Removes an enemy, awards points, and triggers an explosion.
    function removeEnemy(e) {
      spawnExplosion(e.position.x, e.position.y);
      playSfx("enemy");
      scene.remove(e);
      const idx = enemies.indexOf(e);
      if (idx > -1) enemies.splice(idx, 1);
      scoreVal += 100;
      setScore(scoreVal);
    }

    // Removes a gift from the scene and tracking array.
    function removeGift(gift) {
      scene.remove(gift);
      const idx = gifts.indexOf(gift);
      if (idx > -1) gifts.splice(idx, 1);
    }

    // Spawns a gift at a random horizontal position near the top of the screen.
    function spawnGift() {
      const gift = createGift();
      gift.position.set((Math.random() - 0.5) * 16, 7.8, 0);
      scene.add(gift);
      gifts.push(gift);
    }

    // Resets the active power-up timer and state.
    function clearActivePowerUp() {
      activePowerUp = null;
      powerUpTimerSeconds = 0;
    }

    // Applies the fan-shot power-up to the player.
    function applyPowerUp() {
      clearActivePowerUp();
      activePowerUp = "fanShot";
      powerUpTimerSeconds = FAN_SHOT_DURATION_SECONDS;
      playSfx("powerup");
    }

    let t = 0;

    // Runs the main game loop and renders the scene each frame with a continuous background UV scroll.
    function animate() {
      animRef.current = requestAnimationFrame(animate);
      if (gameOver || won) return;
      if (levelCompleted) return;
      const now = performance.now();
      const deltaSeconds = (now - lastFrameTime) / 1000;
      lastFrameTime = now;
      t++;

      // Advances the level every 60 seconds while the player has lives left.
      if (livesVal > 0) {
        levelElapsedSeconds += deltaSeconds;
        levelSecondsLeft = Math.max(0, Math.ceil(LEVEL_DURATION_SECONDS - levelElapsedSeconds));
        if (levelSecondsLeft !== lastDisplayedSeconds) {
          lastDisplayedSeconds = levelSecondsLeft;
          setLevelTimeLeft(levelSecondsLeft);
        }
        if (levelElapsedSeconds >= LEVEL_DURATION_SECONDS) {
          levelCompleted = true;
          levelElapsedSeconds = LEVEL_DURATION_SECONDS;
          levelSecondsLeft = 0;
          setLevelTimeLeft(0);
          setLevelComplete(true);
          triggerLevelCompleteSfx();
        }
      }

      // --- SCROLL BACKGROUND (top to bottom) ---
      updateBackgroundPlanes();

      // --- SCROLL CLOUDS (top to bottom) ---
      updateCloudLayers();

      // Pulsing lights
      pinkLight.intensity = 2.5 + Math.sin(t * 0.05) * 0.8;
      cyanLight.intensity = 1.5 + Math.cos(t * 0.07) * 0.5;

      // Twinkle stars
      stars.rotation.z += 0.0002;

      // Propeller spin
      if (player.propeller) player.propeller.rotation.z += 0.3;
      if (player.trail) player.trail.material.opacity = 0.4 + Math.sin(t * 0.2) * 0.2;

      // --- PLAYER MOVEMENT ---
      if (!gameOver) {
        // Applies a movement speed boost while the fan-shot power-up is active.
        const speedMultiplier = activePowerUp === "fanShot" ? 1.5 : 1;
        if (keysRef.current["ArrowLeft"] || keysRef.current["a"]) playerVel.x -= 0.015 * speedMultiplier;
        if (keysRef.current["ArrowRight"] || keysRef.current["d"]) playerVel.x += 0.015 * speedMultiplier;
        if (keysRef.current["ArrowUp"] || keysRef.current["w"]) playerVel.y += 0.012 * speedMultiplier;
        if (keysRef.current["ArrowDown"] || keysRef.current["s"]) playerVel.y -= 0.012 * speedMultiplier;

        const touchState = touchStateRef.current;
        if (touchState.active) {
          const dx = touchState.target.x - playerPos.x;
          const dy = touchState.target.y - playerPos.y;
          const distance = Math.hypot(dx, dy);
          if (distance > 0.05) {
            const dirX = dx / distance;
            const dirY = dy / distance;
            playerVel.x += dirX * 0.02 * speedMultiplier;
            playerVel.y += dirY * 0.016 * speedMultiplier;
          }
        }

        playerVel.x *= 0.82;
        playerVel.y *= 0.82;

        playerPos.x = Math.max(-9, Math.min(9, playerPos.x + playerVel.x));
        playerPos.y = Math.max(-6.5, Math.min(6.5, playerPos.y + playerVel.y));

        player.position.x = playerPos.x;
        player.position.y = playerPos.y;
        player.rotation.z = -playerVel.x * 2.5;

        // Bank effect
        player.rotation.x = playerVel.y * 1.5;

        // Invincibility flash
        if (invincibleTimer > 0) {
          invincibleTimer--;
          player.visible = Math.floor(invincibleTimer / 6) % 2 === 0;
        } else {
          player.visible = true;
        }

        // --- PLAYER FIRE --- (manual only: spacebar or tap)
        fireTimer++;
        if ((keysRef.current[" "] || keysRef.current["z"] || autoFireRef.current) && fireTimer > 10) {
          fireTimer = 0;
          if (activePowerUp === "fanShot") {
            const shotOffsets = [-0.6, -0.35, -0.1, 0.1, 0.35, 0.6];
            const shotVelocities = [0.18, 0.2, 0.22, 0.22, 0.2, 0.18];
            shotOffsets.forEach((offset, index) => {
              const vx = offset * 0.06;
              const vy = shotVelocities[index];
              const b = spawnBullet(playerPos.x + offset, playerPos.y + 0.6, vy, false);
              b.userData.vx = vx;
            });
          } else {
            spawnBullet(playerPos.x - 0.3, playerPos.y + 0.6, 0.22, false);
            spawnBullet(playerPos.x + 0.3, playerPos.y + 0.6, 0.22, false);
          }
        }
      }

      // --- ENEMIES MOVEMENT ---
      enemies.forEach((e) => {
        const d = e.userData;
        d.t += 0.01 * d.speed;
        let ex, ey;
        if (d.pattern === 0) {
          ex = d.startX + Math.sin(d.t * 2) * 3;
          ey = d.startY - d.t * 30 * 0.01 * d.speed;
        } else if (d.pattern === 1) {
          ex = d.startX + Math.cos(d.t * 1.5) * 4;
          ey = d.startY - d.t * 25 * 0.01 * d.speed;
        } else {
          const angle = d.t * 1.2;
          ex = d.startX + Math.sin(angle) * 3;
          ey = d.startY - d.t * 20 * 0.01 * d.speed + Math.cos(angle) * 1.5;
        }
        // Keeps enemy X positions inside the visible screen edges while still allowing edge travel.
        ex = Math.max(-9.5, Math.min(9.5, ex));
        // Clamp: never go back above starting Y (only move downward)
        ey = Math.min(ey, d.startY);
        // Snap the entry so enemies fully enter the screen within the target duration.
        const entryBottomY = 7.5 - 0.5;
        const entryProgress = Math.min(1, d.t / ENEMY_ENTRY_DURATION_SECONDS);
        const entryY = d.startY + (entryBottomY - d.startY) * entryProgress;
        ey = Math.min(ey, entryY);
        e.position.set(ex, ey, 0);
        e.rotation.z = Math.sin(d.t * 3) * 0.15;

        // Enemy fire (only when fully visible on screen)
        d.fireTimer++;
        const enemyHalfHeight = 0.5;
        // Requires enemies to be within both vertical and horizontal view bounds before firing.
        const onScreen =
          ey - enemyHalfHeight >= -7.5 &&
          ey + enemyHalfHeight <= 7.5 &&
          e.position.x >= -10 &&
          e.position.x <= 10;
        if (onScreen && d.fireTimer > d.fireRate) {
          d.fireTimer = 0;
          spawnBullet(e.position.x, e.position.y - 0.5, -0.12, true);
        }

        // Off bottom -> remove silently (no points)
        if (ey < -8) {
          scene.remove(e);
          enemies.splice(enemies.indexOf(e), 1);
        }
      });

      // --- SPAWN NEW WAVES ---
      waveTimer++;
      waveTimerSeconds += deltaSeconds;
      if (waveTimerSeconds >= WAVE_INTERVAL_SECONDS) {
        waveTimerSeconds = 0;
        spawnWave();
      }

      // --- PLAYER BULLETS ---
      playerBullets.slice().forEach((b) => {
        if (b.userData.vx) b.position.x += b.userData.vx;
        b.position.y += b.userData.vy;
        if (b.userData.glow) b.userData.glow.position.copy(b.position);
        if (b.position.y > 8 || b.position.x < -10 || b.position.x > 10) { removeBullet(playerBullets, b); return; }

        // Hit enemies
        enemies.slice().forEach((e) => {
          const dx = b.position.x - e.position.x;
          const dy = b.position.y - e.position.y;
          if (Math.abs(dx) < 0.7 && Math.abs(dy) < 0.7) {
            removeBullet(playerBullets, b);
            removeEnemy(e);
          }
        });
      });

      // --- ENEMY BULLETS ---
      enemyBullets.slice().forEach((b) => {
        b.position.y += b.userData.vy;
        if (b.userData.glow) b.userData.glow.position.copy(b.position);
        if (b.userData.flame) b.userData.flame.position.set(b.position.x, b.position.y + 0.32, b.position.z);
        if (b.userData.flameGlow) b.userData.flameGlow.position.set(b.position.x, b.position.y + 0.38, b.position.z);
        if (b.position.y < -8) { removeBullet(enemyBullets, b); return; }

        // Hit player
        if (invincibleTimer === 0) {
          const dx = b.position.x - playerPos.x;
          const dy = b.position.y - playerPos.y;
          if (Math.abs(dx) < 0.45 && Math.abs(dy) < 0.55) {
            removeBullet(enemyBullets, b);
            spawnExplosion(playerPos.x, playerPos.y);
            playSfx("player");
            invincibleTimer = 150;
            livesVal--;
            setLives(livesVal);
            if (livesVal > 0) {
              triggerDeathMessage();
            }
            if (livesVal <= 0) {
              gameOver = true;
              triggerGameOverSfx();
              setGameState("dead");
              setHighScore(h => Math.max(h, scoreVal));
            }
          }
        }
      });

      // Enemy collision with player
      if (invincibleTimer === 0) {
        enemies.slice().forEach((e) => {
          const dx = e.position.x - playerPos.x;
          const dy = e.position.y - playerPos.y;
        if (Math.abs(dx) < 0.7 && Math.abs(dy) < 0.75) {
          removeEnemy(e);
          spawnExplosion(playerPos.x, playerPos.y);
          playSfx("player");
          invincibleTimer = 150;
          livesVal--;
          setLives(livesVal);
          if (livesVal > 0) {
            triggerDeathMessage();
          }
          if (livesVal <= 0) {
            gameOver = true;
            triggerGameOverSfx();
            setGameState("dead");
            setHighScore(h => Math.max(h, scoreVal));
          }
        }
});
      }

      // --- GIFTS ---
      gifts.slice().forEach((gift) => {
        gift.position.y -= POWERUP_SPEED;
        if (gift.userData.glow) gift.userData.glow.material.opacity = 0.25 + Math.sin(t * 0.2) * 0.1;
        if (gift.position.y < -8) { removeGift(gift); return; }
        const dx = gift.position.x - playerPos.x;
        const dy = gift.position.y - playerPos.y;
        if (Math.abs(dx) < 0.8 && Math.abs(dy) < 0.8) {
          removeGift(gift);
          applyPowerUp();
        }
      });

      // --- EXPLOSIONS ---
      explosions.slice().forEach((particles, ei) => {
        let alive = false;
        particles.forEach((p) => {
          p.position.x += p.userData.vx;
          p.position.y += p.userData.vy;
          p.userData.vy -= 0.001;
          p.userData.life -= 0.025;
          p.material.opacity = p.userData.life;
          p.scale.setScalar(p.userData.life);
          if (p.userData.life > 0) alive = true;
        });
        if (!alive) {
          particles.forEach(p => scene.remove(p));
          explosions.splice(ei, 1);
        }
      });

      // --- POWER-UP TIMERS ---
      if (activePowerUp) {
        powerUpTimerSeconds -= deltaSeconds;
        if (powerUpTimerSeconds <= 0) {
          clearActivePowerUp();
        }
      }
      // --- SPAWN GIFTS ---
      powerUpSpawnTimerSeconds += deltaSeconds;
      if (powerUpSpawnTimerSeconds >= POWERUP_SPAWN_INTERVAL_SECONDS) {
        powerUpSpawnTimerSeconds = 0;
        spawnGift();
      }

      renderer.render(scene, camera);
    }

    // Starts the next level after completion.
    function goToNextLevel() {
      if (levelCompleted) {
        levelCompleted = false;
        levelCompleteSfxPlayedRef.current = false;
        levelVal++;
        levelSecondsLeft = LEVEL_DURATION_SECONDS;
        levelElapsedSeconds = 0;
        lastDisplayedSeconds = LEVEL_DURATION_SECONDS;
        lastFrameTime = performance.now();
        waveTimerSeconds = 0;
        setLevel(levelVal);
        setLevelTimeLeft(levelSecondsLeft);
        setLevelComplete(false);
        setBackgroundForLevel(levelVal);
        spawnWave();
      }
    }

    // Exposes the next-level transition to the UI layer.
    gameRef.current.goToNextLevel = goToNextLevel;

    animate();

    // Handles keyboard input state changes.
    const handleKey = (e, down) => {
      keysRef.current[e.key] = down;
      if (e.key === " ") e.preventDefault();
    };
    // Converts touch coordinates into the game world space.
    const screenToWorld = (clientX, clientY) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const normalizedX = ((clientX - rect.left) / rect.width) * 2 - 1;
      const normalizedY = -(((clientY - rect.top) / rect.height) * 2 - 1);
      return {
        x: normalizedX * 10,
        y: normalizedY * 7.5,
      };
    };
    // Handles touch input mapped to fire action and movement.
    const handleTouchStart = (e) => {
      if (e.cancelable) e.preventDefault();
      const touch = e.changedTouches[0];
      if (!touch) return;
      touchStateRef.current.active = true;
      touchStateRef.current.touchId = touch.identifier;
      keysRef.current[" "] = true;
      const target = screenToWorld(touch.clientX, touch.clientY);
      touchStateRef.current.target = {
        x: Math.max(-9, Math.min(9, target.x)),
        y: Math.max(-6.5, Math.min(6.5, target.y)),
      };
    };
    const handleTouchMove = (e) => {
      if (!touchStateRef.current.active) return;
      if (e.cancelable) e.preventDefault();
      const touches = Array.from(e.changedTouches || []);
      const activeTouch = touches.find((t) => t.identifier === touchStateRef.current.touchId) || e.touches[0];
      if (!activeTouch) return;
      const target = screenToWorld(activeTouch.clientX, activeTouch.clientY);
      touchStateRef.current.target = {
        x: Math.max(-9, Math.min(9, target.x)),
        y: Math.max(-6.5, Math.min(6.5, target.y)),
      };
    };
    const handleTouchEnd = (e) => {
      if (e.cancelable) e.preventDefault();
      touchStateRef.current.active = false;
      touchStateRef.current.touchId = null;
      keysRef.current[" "] = false;
    };
    const resizeRenderer = () => {
      if (!container) return;
      const { clientWidth, clientHeight } = container;
      if (clientWidth > 0 && clientHeight > 0) {
        renderer.setSize(clientWidth, clientHeight, false);
      }
    };
    resizeRenderer();

    window.addEventListener("keydown", (e) => handleKey(e, true));
    window.addEventListener("keyup", (e) => handleKey(e, false));
    window.addEventListener("resize", resizeRenderer);
    renderer.domElement.addEventListener("touchstart", handleTouchStart, { passive: false });
    renderer.domElement.addEventListener("touchmove", handleTouchMove, { passive: false });
    renderer.domElement.addEventListener("touchend", handleTouchEnd, { passive: false });
    renderer.domElement.addEventListener("touchcancel", handleTouchEnd, { passive: false });

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("keydown", (e) => handleKey(e, true));
      window.removeEventListener("keyup", (e) => handleKey(e, false));
      window.removeEventListener("resize", resizeRenderer);
      renderer.domElement.removeEventListener("touchstart", handleTouchStart);
      renderer.domElement.removeEventListener("touchmove", handleTouchMove);
      renderer.domElement.removeEventListener("touchend", handleTouchEnd);
      renderer.domElement.removeEventListener("touchcancel", handleTouchEnd);
      backgroundPlane1.geometry.dispose();
      backgroundPlane2.geometry.dispose();
      backgroundMaterial.dispose();
      backgroundTextures.forEach((texture) => texture.dispose());
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      gameRef.current.goToNextLevel = null;
    };
  }, [gameState, triggerDeathMessage]);

  // Displays the player lives as airplane icons.
  const HeartIcons = ({ lives }) => (
    <div style={{ display: "flex", gap: 6 }}>
      {[...Array(3)].map((_, i) => (
        <span key={i} style={{
          fontSize: 22,
          opacity: i < lives ? 1 : 0.2,
          filter: i < lives ? "drop-shadow(0 0 6px #ff00ff)" : "none",
          transition: "all 0.3s"
        }}>✈</span>
      ))}
    </div>
  );

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      background: "linear-gradient(180deg, #0a0015 0%, #1a0033 50%, #0a001a 100%)",
      fontFamily: "'Courier New', monospace",
      userSelect: "none",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Scanline overlay */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 99,
        background: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)",
      }} />

      {/* Title */}
      <div style={{
        textAlign: "center",
        marginBottom: 16,
        position: "relative",
        zIndex: 10,
      }}>
        <div style={{
          fontSize: 42,
          fontWeight: 900,
          letterSpacing: 12,
          background: "linear-gradient(90deg, #ff00ff, #00ffff, #ff00ff)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textShadow: "none",
          filter: "drop-shadow(0 0 20px #ff00ff)",
          animation: "pulse 2s ease-in-out infinite",
        }}>1943</div>
        <div style={{
          fontSize: 11,
          color: "#ff00ff",
          letterSpacing: 6,
          opacity: 0.8,
          textTransform: "uppercase",
          textShadow: "0 0 10px #ff00ff",
        }}>SYNTHWAVE SQUADRON</div>
      </div>

      {/* HUD */}
      {gameState === "playing" && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          width: "min(100vw, 800px)",
          maxWidth: 800,
          padding: "6px 16px",
          background: "rgba(255,0,255,0.07)",
          border: "1px solid rgba(255,0,255,0.3)",
          borderBottom: "none",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 10,
        }}>
          <HeartIcons lives={lives} />
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <label style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#00ffff",
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              textShadow: "0 0 8px #00ffff",
            }}>
              <input
                type="checkbox"
                checked={autoFireEnabled}
                onChange={handleAutoFireToggle}
                style={{ accentColor: "#00ffff" }}
              />
              Auto-fire
            </label>
            <label style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#00ffff",
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              textShadow: "0 0 8px #00ffff",
            }}>
              <input
                type="checkbox"
                checked={soundtrackEnabled}
                onChange={handleSoundtrackToggle}
                style={{ accentColor: "#00ffff" }}
              />
              Soundtrack
            </label>
          </div>
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}>
            <div style={{
              color: "#ffff00",
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 2,
              textShadow: "0 0 10px #ffff00",
            }}>
              LEVEL {level}
            </div>
            <div style={{
              color: "#ff00ff",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 2,
              textShadow: "0 0 8px #ff00ff",
            }}>
              TIME {String(levelTimeLeft).padStart(2, "0")}
            </div>
          </div>
          <div style={{
            color: "#00ffff",
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: 3,
            textShadow: "0 0 12px #00ffff",
          }}>
            {String(score).padStart(6, "0")}
          </div>
        </div>
      )}

      {/* Game canvas */}
      <div style={{
        position: "relative",
        border: "2px solid",
        borderColor: gameState === "playing" ? "#ff00ff" : "#ff00ff88",
        boxShadow: "0 0 40px #ff00ff55, inset 0 0 20px #00000055",
        transition: "border-color 0.3s",
        width: "min(100vw, 800px)",
        maxWidth: 800,
        aspectRatio: "4 / 3",
        maxHeight: "90vh",
      }}>
        <div ref={mountRef} style={{ width: "100%", height: "100%", display: "block" }} />

        {/* DEATH MESSAGE */}
        {gameState === "playing" && deathMessageVisible && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 15,
          }}>
            <div style={{
              fontSize: 36,
              fontWeight: 900,
              color: "#ff0033",
              textShadow: "0 0 20px #ff0033, 0 0 40px #ff0033",
              letterSpacing: 4,
              textTransform: "uppercase",
            }}>
              You're dead!
            </div>
          </div>
        )}

        {/* LEVEL COMPLETE */}
        {gameState === "playing" && levelComplete && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            textAlign: "center",
            background: "rgba(10,0,21,0.92)",
            zIndex: 20,
          }}>
            <div style={{
              fontSize: 46, fontWeight: 900,
              color: "#ffff00",
              textShadow: "0 0 30px #ffff00, 0 0 60px #ffff0066",
              letterSpacing: 6, marginBottom: 20,
            }}>LEVEL COMPLETE!</div>
            <button onClick={() => {
              if (gameRef.current.goToNextLevel) {
                gameRef.current.goToNextLevel();
              }
            }} style={{
              background: "transparent",
              border: "2px solid #ffff00",
              color: "#ffff00",
              padding: "12px 44px",
              fontSize: 16,
              letterSpacing: 6,
              cursor: "pointer",
              textShadow: "0 0 12px #ffff00",
              boxShadow: "0 0 20px #ffff0044",
              fontFamily: "inherit",
              transition: "all 0.2s",
            }}
              onMouseOver={e => { e.target.style.background = "#ffff0022"; }}
              onMouseOut={e => { e.target.style.background = "transparent"; }}
            >GO ON</button>
          </div>
        )}

        {/* START SCREEN */}
        {gameState === "start" && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            background: "rgba(10,0,21,0.88)",
            zIndex: 20,
          }}>
            <div style={{ fontSize: 56, marginBottom: 8 }}>✈</div>
            <div style={{
              fontSize: 18, color: "#ff00ff", letterSpacing: 4,
              textShadow: "0 0 20px #ff00ff", marginBottom: 6,
              animation: "blink 1.2s step-end infinite",
            }}>WAR ARCADE</div>
            <div style={{
              color: "#00ffff", fontSize: 13, letterSpacing: 2,
              textShadow: "0 0 10px #00ffff", marginBottom: 40,
            }}>SURVIVE 30 SECONDS</div>
            <button onClick={startGame} style={{
              background: "transparent",
              border: "2px solid #ff00ff",
              color: "#ff00ff",
              padding: "14px 48px",
              fontSize: 18,
              letterSpacing: 6,
              cursor: "pointer",
              textShadow: "0 0 12px #ff00ff",
              boxShadow: "0 0 20px #ff00ff44",
              transition: "all 0.2s",
              fontFamily: "inherit",
            }}
              onMouseOver={e => { e.target.style.background = "#ff00ff22"; e.target.style.boxShadow = "0 0 40px #ff00ffaa"; }}
              onMouseOut={e => { e.target.style.background = "transparent"; e.target.style.boxShadow = "0 0 20px #ff00ff44"; }}
            >INSERT COIN</button>
            <div style={{ marginTop: 30, color: "#ffffff44", fontSize: 11, letterSpacing: 2 }}>
              WASD / ARROWS TO MOVE · SPACE TO FIRE
            </div>
            <div style={{ marginTop: 14, display: "flex", flexDirection: "row", gap: 10 }}>
              <label style={{
                display: "flex",                
                alignItems: "center",
                gap: 10,
                color: "#00ffff",
                fontSize: 12,
                letterSpacing: 3,
                textTransform: "uppercase",
                textShadow: "0 0 10px #00ffff",
              }}>
                <input
                  type="checkbox"
                  checked={autoFireEnabled}
                  onChange={handleAutoFireToggle}
                  style={{ accentColor: "#00ffff" }}
                />
                Auto-fire
              </label>
              <label style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "#00ffff",
                fontSize: 12,
                letterSpacing: 3,
                textTransform: "uppercase",
                textShadow: "0 0 10px #00ffff",
              }}>
                <input
                  type="checkbox"
                  checked={soundtrackEnabled}
                  onChange={handleSoundtrackToggle}
                  style={{ accentColor: "#00ffff" }}
                />
                Soundtrack
              </label>
            </div>
          </div>
        )}

        {/* GAME OVER */}
        {gameState === "dead" && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            background: "rgba(10,0,21,0.92)",
            zIndex: 20,
          }}>
            <div style={{
              fontSize: 52, fontWeight: 900,
              color: "#ff0055",
              textShadow: "0 0 30px #ff0055, 0 0 60px #ff005588",
              letterSpacing: 8, marginBottom: 16,
              animation: "pulse 1s ease-in-out infinite",
            }}>GAME OVER</div>
            <div style={{ color: "#ff00ff", fontSize: 16, letterSpacing: 4, marginBottom: 6, textShadow: "0 0 10px #ff00ff" }}>
              SCORE: {String(score).padStart(6, "0")}
            </div>
            <div style={{ color: "#00ffff", fontSize: 13, letterSpacing: 4, marginBottom: 40, textShadow: "0 0 8px #00ffff" }}>
              BEST: {String(highScore).padStart(6, "0")}
            </div>
            <button onClick={startGame} style={{
              background: "transparent",
              border: "2px solid #ff0055",
              color: "#ff0055",
              padding: "12px 40px",
              fontSize: 16,
              letterSpacing: 5,
              cursor: "pointer",
              textShadow: "0 0 12px #ff0055",
              boxShadow: "0 0 20px #ff005544",
              fontFamily: "inherit",
              transition: "all 0.2s",
            }}
              onMouseOver={e => { e.target.style.background = "#ff005522"; }}
              onMouseOut={e => { e.target.style.background = "transparent"; }}
            >TRY AGAIN</button>
          </div>
        )}

        {/* WIN SCREEN */}
        {gameState === "win" && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            background: "rgba(10,0,21,0.92)",
            zIndex: 20,
          }}>
            <div style={{
              fontSize: 44, fontWeight: 900,
              background: "linear-gradient(90deg, #ff00ff, #00ffff, #ffff00)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 0 20px #ff00ff)",
              letterSpacing: 6, marginBottom: 16,
            }}>MISSION COMPLETE</div>
            <div style={{ color: "#ffff00", fontSize: 16, letterSpacing: 4, marginBottom: 6, textShadow: "0 0 10px #ffff00" }}>
              SCORE: {String(score).padStart(6, "0")}
            </div>
            <div style={{ color: "#00ffff", fontSize: 13, letterSpacing: 4, marginBottom: 40, textShadow: "0 0 8px #00ffff" }}>
              BEST: {String(highScore).padStart(6, "0")}
            </div>
            <button onClick={startGame} style={{
              background: "transparent",
              border: "2px solid #00ffff",
              color: "#00ffff",
              padding: "12px 40px",
              fontSize: 16,
              letterSpacing: 5,
              cursor: "pointer",
              textShadow: "0 0 12px #00ffff",
              boxShadow: "0 0 20px #00ffff44",
              fontFamily: "inherit",
              transition: "all 0.2s",
            }}
              onMouseOver={e => { e.target.style.background = "#00ffff22"; }}
              onMouseOut={e => { e.target.style.background = "transparent"; }}
            >PLAY AGAIN</button>
          </div>
        )}
      </div>

      {/* Controls hint */}
      <div style={{
        marginTop: 12, color: "#ffffff33", fontSize: 11,
        letterSpacing: 3, textAlign: "center",
      }}>
        WASD / ↑↓←→ MOVE &nbsp;
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
